import type React from "react";
import { useState } from "react";
import { useOptionalTransfer } from "../../TransferButton/TransferContext";

// Post-review suggestion card: orders extracted from the just-generated note,
// offered for one-tap placement. Confirm dispatches `place_suggested_orders`
// with the SELECTED orders verbatim — the backend runs the workflow directly
// (no chat-LLM round trip) and narrates progress into the thread.
//
// When the note hasn't been transferred yet (note_transferred=false), the
// primary action is "Transfer note & place orders": the existing transfer
// flow starts (banner / appointment / MFA modals unchanged), then
// `queue_orders_after_transfer` stores the orders on the job — the backend
// places them once the note lands, on the same encounter, even if the
// clinician navigates away.
//
// Corrections happen in chat ("drop the lipid panel"): the backend re-emits
// this widget with the revised list and the older card renders superseded.
export interface SuggestedOrder {
  test_name: string;
  order_type?: string;
  search_aliases?: string[];
}

export interface OrderSuggestionsData {
  audio_file_id?: number;
  ehr_label?: string;
  question?: string;
  orders?: SuggestedOrder[];
  note_transferred?: boolean;
}

interface OrderSuggestionsProps {
  data: OrderSuggestionsData;
  onCustomAction: (action: { type: string; payload?: Record<string, unknown> }) => void;
  disabled?: boolean;
  // A newer order_suggestions card exists in the thread (a chat revision) —
  // render collapsed so two interactive lists never compete.
  superseded?: boolean;
}

type Phase = "input" | "submitted" | "combined_submitted" | "dismissed";

const ORDER_TYPE_LABELS: Record<string, string> = {
  lab: "lab",
  di: "imaging",
  procedure: "procedure",
};

const BoltIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
    <path d="M8.5 1.5 3 9h4l-.5 5.5L13 7H8.5l0-5.5Z" stroke="#6366F1" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>
);

const OrderSuggestions: React.FC<OrderSuggestionsProps> = ({ data, onCustomAction, disabled, superseded }) => {
  const orders = data?.orders ?? [];
  const transfer = useOptionalTransfer();
  const [phase, setPhase] = useState<Phase>("input");
  const [isStartingTransfer, setIsStartingTransfer] = useState(false);
  // All checked by default — the card exists because the note says to order these.
  const [selected, setSelected] = useState<Set<string>>(() => new Set(orders.map((o) => o.test_name)));

  if (!orders.length) return null;

  if (superseded) {
    return (
      <div style={{ ...styles.container, ...styles.dismissedBox }}>
        <div style={styles.header}>
          <span style={styles.dismissedText}>Updated — see the newer list below.</span>
        </div>
      </div>
    );
  }

  const noteTransferred = !!data.note_transferred;
  const canCombine = !noteTransferred && !!transfer && !!data.audio_file_id;

  const toggle = (name: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });

  const selectedOrders = () => orders.filter((o) => selected.has(o.test_name));

  const place = () => {
    if (disabled || !selected.size) return;
    onCustomAction({
      type: "place_suggested_orders",
      payload: {
        audio_file_id: data.audio_file_id,
        ehr_label: data.ehr_label,
        orders: selectedOrders(),
      },
    });
    setPhase("submitted");
  };

  const transferAndPlace = async () => {
    if (disabled || !selected.size || !transfer || !data.audio_file_id || isStartingTransfer) return;
    setIsStartingTransfer(true);
    try {
      const result = await transfer.startTransfer(data.audio_file_id);
      if (result.jobId) {
        // Fresh transfer OR one already in flight ("in_progress") — either
        // way the backend chains the orders behind that job's completion.
        onCustomAction({
          type: "queue_orders_after_transfer",
          payload: {
            audio_file_id: data.audio_file_id,
            transfer_job_id: result.jobId,
            orders: selectedOrders(),
          },
        });
        setPhase("combined_submitted");
      } else if (result.code === "already_transferred") {
        // The note landed since this card was built — just place the orders
        // (the backend reuses the transfer's encounter).
        place();
      }
      // Any other failure: startTransfer already surfaced the error toast;
      // the card stays interactive for a retry.
    } finally {
      setIsStartingTransfer(false);
    }
  };

  const dismiss = () => {
    if (disabled) return;
    onCustomAction({ type: "order_suggestions_dismiss" });
    setPhase("dismissed");
  };

  if (phase === "submitted") {
    return (
      <div style={{ ...styles.container, ...styles.resolved }}>
        <div style={styles.header}>
          <BoltIcon />
          <span style={styles.headerText}>On it — I'll keep you posted in the chat.</span>
        </div>
      </div>
    );
  }

  if (phase === "combined_submitted") {
    return (
      <div style={{ ...styles.container, ...styles.resolved }}>
        <div style={styles.header}>
          <BoltIcon />
          <span style={styles.headerText}>Transferring the note first, then placing the orders — you can move on.</span>
        </div>
      </div>
    );
  }

  if (phase === "dismissed") {
    return (
      <div style={{ ...styles.container, ...styles.dismissedBox }}>
        <div style={styles.header}>
          <span style={styles.dismissedText}>Dismissed — nothing was ordered.</span>
        </div>
      </div>
    );
  }

  const busy = disabled || isStartingTransfer;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <BoltIcon />
        <span style={styles.headerText}>
          {data.question || `This note's plan includes orders I can place in ${data.ehr_label || "your EHR"}:`}
        </span>
      </div>
      <div style={styles.checkGroup}>
        {orders.map((o) => (
          <label key={o.test_name} style={styles.checkRow}>
            <input
              type="checkbox"
              disabled={busy}
              checked={selected.has(o.test_name)}
              onChange={() => toggle(o.test_name)}
            />
            <span>
              {o.test_name}
              {o.order_type && ORDER_TYPE_LABELS[o.order_type] ? ` (${ORDER_TYPE_LABELS[o.order_type]})` : ""}
            </span>
          </label>
        ))}
      </div>
      <div style={styles.hint}>Need changes? Tell me below and I'll update this list.</div>
      <div style={styles.actions}>
        {canCombine ? (
          <>
            <button
              type="button"
              disabled={busy || !selected.size}
              onClick={transferAndPlace}
              style={{
                ...styles.button,
                ...styles.confirmButton,
                ...(busy || !selected.size ? styles.buttonDisabled : {}),
              }}
            >
              {isStartingTransfer ? "Starting…" : "Transfer note & place orders"}
            </button>
            <button
              type="button"
              disabled={busy || !selected.size}
              onClick={place}
              style={{
                ...styles.button,
                ...styles.cancelButton,
                ...(busy || !selected.size ? styles.buttonDisabled : {}),
              }}
            >
              {selected.size === 1 ? "Place order only" : "Place orders only"}
            </button>
          </>
        ) : (
          <button
            type="button"
            disabled={busy || !selected.size}
            onClick={place}
            style={{
              ...styles.button,
              ...styles.confirmButton,
              ...(busy || !selected.size ? styles.buttonDisabled : {}),
            }}
          >
            {selected.size === 1 ? "Place selected order" : "Place selected orders"}
          </button>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={dismiss}
          style={{ ...styles.button, ...styles.cancelButton, ...(busy ? styles.buttonDisabled : {}) }}
        >
          Not now
        </button>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "12px 14px",
    marginTop: "8px",
    borderRadius: "12px",
    border: "1px solid #C7D2FE",
    backgroundColor: "#F5F3FF",
  },
  resolved: { backgroundColor: "#F0FDF4", border: "1px solid #BBF7D0" },
  dismissedBox: { backgroundColor: "#F9FAFB", border: "1px solid #E5E7EB" },
  dismissedText: { fontSize: "13px", fontWeight: 500, color: "#6B7280", lineHeight: 1.4 },
  header: { display: "flex", alignItems: "center", gap: "8px" },
  headerText: { fontSize: "13px", fontWeight: 600, color: "#111827", lineHeight: 1.4 },
  checkGroup: { display: "flex", flexDirection: "column", gap: "4px", paddingLeft: "2px" },
  checkRow: { display: "flex", alignItems: "center", gap: "7px", fontSize: "13px", color: "#374151" },
  hint: { fontSize: "12px", color: "#6B7280", lineHeight: 1.4 },
  actions: { display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" },
  button: {
    flex: "0 1 auto",
    padding: "8px 14px",
    fontSize: "13px",
    fontWeight: 500,
    borderRadius: "8px",
    border: "1px solid transparent",
    cursor: "pointer",
    fontFamily: "inherit",
    marginTop: "4px",
  },
  confirmButton: { backgroundColor: "#4338CA", color: "#FFFFFF", borderColor: "#4338CA" },
  cancelButton: { backgroundColor: "#FFFFFF", color: "#374151", borderColor: "#D1D5DB" },
  buttonDisabled: { opacity: 0.6, cursor: "not-allowed" },
};

export default OrderSuggestions;
